import React, { useEffect, useRef } from 'react';
import type { BallTrack } from '../lib/BallTracker';
import type { HandPosition } from '../lib/HandTracker';

interface TrackingOverlayProps {
  width: number;
  height: number;
  tracks: BallTrack[];
  hands: HandPosition[];
  trailHistory: Array<{ id: number; x: number; y: number }[]>;
}

const BALL_COLORS = ['#ef4444', '#3b82f6', '#eab308'];

export const TrackingOverlay = ({
  width,
  height,
  tracks,
  hands,
  trailHistory,
}: TrackingOverlayProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    for (let i = 0; i < trailHistory.length; i++) {
      const trail = trailHistory[i];
      if (trail.length < 2) continue;
      const color = BALL_COLORS[(trail[0]?.id ?? 0) % BALL_COLORS.length] ?? '#22c55e';
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(trail[0].x, trail[0].y);
      for (let j = 1; j < trail.length; j++) {
        ctx.lineTo(trail[j].x, trail[j].y);
      }
      ctx.stroke();
    }

    for (const track of tracks) {
      const color = BALL_COLORS[(track.id - 1) % BALL_COLORS.length] ?? '#22c55e';
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(track.x, track.y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    for (const hand of hands) {
      ctx.fillStyle = hand.isLeft ? '#06b6d4' : '#8b5cf6';
      ctx.font = '14px sans-serif';
      ctx.fillText(hand.isLeft ? 'L' : 'R', hand.x - 4, hand.y + 4);
      ctx.beginPath();
      ctx.arc(hand.x, hand.y, 8, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, [width, height, tracks, hands, trailHistory]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute top-0 left-0 pointer-events-none"
      style={{ width, height }}
      aria-hidden
    />
  );
};
